import React, { FormEvent, useState } from 'react';
import {
	ApplicationStatus,
	PositionTitle,
	UIApplication,
	UIExtendedApplication,
	UIRemarksDrawerType,
	UIUser,
	UserRole,
	WorkingMode,
} from 'types';
import { generatePositionTitle } from 'shared-utils';
import { Button, FileInput, Label, Modal, Spinner } from 'flowbite-react';
import { pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';

// Core viewer
import { Viewer } from '@react-pdf-viewer/core';

// Plugins
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';

// Import styles
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import { useMutation, useQueryClient } from 'react-query';
import { useToast } from 'src/context/ToastProvider';
import {
	acceptChangedApplicationTerms,
	approveAction,
	offerLetterResponse,
	rejectAction,
	requestOptingOutOfApplicationProcess,
	sendOfferLetter,
} from 'src/apis/application';
import { useNavigate, useSearchParams } from 'react-router-dom';
import OptOutModal from '../application-card-v2/OptOutModal';
import { useUserContext } from 'src/context/UserProvider';
import RemarksDrawer from '../application-card-v2/RemarksDrawer';
import { Drawer } from 'src/components/shared/Drawer';
import RecruiterCandidateSendOfferLetter from 'src/components/recruiterPositions/RecruiterCandidateSendOfferLetter';
import OfferedPositionParameters from './OfferedPositionParameters';
import { Img } from 'react-image';
import { useMediaQuery } from '@mui/material';
import { useThemeContext } from 'src/context/ThemeProvider';
import { getPolicyForS3NonVideo } from 'src/apis/user';
import { handleS3Upload } from 'src/utils/imageUpload';

// Required for rendering PDFs in a browser
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

interface Props {
	application: UIApplication | UIExtendedApplication;
	companyName?: string;
	positionTitle?: string;
	positionId?: string;
	jobSeekerUser?: UIUser;
	positionWorkingMode?: WorkingMode;
	onFinish?: () => void;
}

const ViewOffer = ({
	jobSeekerUser,
	positionId,
	application,
	positionWorkingMode,
	companyName: company,
	positionTitle: position,
	onFinish,
}: Props) => {
	// Create new plugin instance
	const { user } = useUserContext();
	const { themeMode } = useThemeContext();
	const { isJobseekerLoggedIn, isAccountManagerLoggedIn, isRecruiterLoggedIn } = useUserContext();
	const defaultLayoutPluginInstance = defaultLayoutPlugin();
	const companyName = application?.position?.company?.name || company || '';
	const toast = useToast();
	const queryClient = useQueryClient();
	const positionTitle = generatePositionTitle((application?.position?.title || position) as PositionTitle);
	const [, setofferLetterResponseLoading] = React.useState(false);
	const [searchParams, setSearchParams] = useSearchParams();
	const navigate = useNavigate();
	const [file, setFile] = React.useState<File | null>();
	const [declineReason, setDeclineReason] = useState<string>('');
	const [showDeclineModal, setShowDeclineModal] = useState(false);
	const [showAcceptConfirmModal, setShowAcceptConfirmModal] = useState(false);
	const [showDeclineConfirmModal, setShowDeclineConfirmModal] = useState(false);
	const acceptChangedApplicationTermsMutation = useMutation(
		['acceptChangedApplicationTerms'],
		acceptChangedApplicationTerms,
		{
			onSuccess() {
				toast({
					title: 'Action Successful!',
					type: 'success',
				});
				onComplete();
				queryClient.invalidateQueries(['getAllApplications']);
				queryClient.refetchQueries(['getAllApplications']);

				const newSearchParams = new URLSearchParams(searchParams);
				newSearchParams.delete('viewOffer');
				setSearchParams(newSearchParams);
				navigate(`/my-applications?${newSearchParams.toString()}`);
			},
			onError(error: Error) {
				onComplete();
				toast({
					title: `Error while accepting changed application terms: ${error.message}`,
					type: 'error',
				});
			},
		},
	);

	const sendOfferLetterByJobseekerFn = useMutation(['sendOfferLetter'], sendOfferLetter, {
		onSuccess() {
			toast({
				title: 'Offer link updated',
				type: 'success',
			});
			queryClient.refetchQueries(['getAllApplications']);
			setFile(null);
		},
		onError(error: Error) {
			toast({
				title: `Error occured ${error.message}`,
				type: 'error',
			});
		},
	});

	const handleSubmitJobseekrOfferLetter = async (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		let imageUrl = '';
		if (file) {
			const fileSizeMB = file.size / (1024 * 1024);
			if (fileSizeMB > 5) {
				toast({
					type: 'error',
					title: 'File size exceeds the maximum limit of 5 MB',
					duration: 10000,
				});
				return;
			}

			setIsLoading(true);

			const policy = await getPolicyForS3NonVideo(
				file.name.trim().split(' ').join('_'),
				'offerLetters',
				file.type,
				true,
			);
			const uploadedUrl = await handleS3Upload(policy, file);
			setIsLoading(false);
			if (uploadedUrl) imageUrl = uploadedUrl;
		}
		const updatedOfferLetterLink = imageUrl;

		sendOfferLetterByJobseekerFn.mutate({
			applicationId: application?._id?.toString() as string,
			positionId: positionId || (application?.position?._id?.toString() as string),
			offerLetterLink: updatedOfferLetterLink,
		});
	};

	const requestOptingOutOfApplicationProcessMutation = useMutation(
		['optOutOfApplicationProcess'],
		requestOptingOutOfApplicationProcess,
		{
			onSuccess() {
				toast({
					title: 'You have successfully requested to opt out of the application process',
					type: 'success',
				});
				onComplete();
				queryClient.invalidateQueries(['getAllApplications']);
				queryClient.refetchQueries(['getAllApplications']);

				const newSearchParams = new URLSearchParams(searchParams);
				newSearchParams.delete('viewOffer');
				setSearchParams(newSearchParams);
				navigate(`/my-applications?${newSearchParams.toString()}`);
			},
			onError(error: Error) {
				onComplete();
				toast({
					title: `Error while requesting opt out of the application process: ${error.message}`,
					type: 'error',
				});
			},
		},
	);

	const offerLetterResponseMutation = useMutation(['offerLetterResponse'], offerLetterResponse, {
		onSuccess() {
			toast({
				title: `Offer letter response sent`,
				type: 'success',
			});
			queryClient.refetchQueries(['getAllApplications']);
			const newSearchParams = new URLSearchParams(searchParams);
			newSearchParams.delete('viewOffer');
			setSearchParams(newSearchParams);

			if (onFinish) {
				onFinish();
			} else {
				navigate(`/my-applications?${newSearchParams.toString()}`);
				setofferLetterResponseLoading(false);
			}
		},
		onError(error: Error) {
			setofferLetterResponseLoading(false);
			toast({
				title: `Error: ${error.message}`,
				type: 'error',
			});
		},
	});
	const [isLoading, setIsLoading] = useState(false);
	const onComplete = () => {
		setIsLoading(false);
		setRemarksDrawer(false);
		setRemarks('');
	};
	const rejectActionMutation = useMutation(['rejectAction'], rejectAction, {
		onSuccess() {
			toast({
				title: 'Action successfull!',
				type: 'success',
			});
			onComplete();
			queryClient.invalidateQueries(['getRecommendationsForPosition', positionId]);
			queryClient.refetchQueries(['getRecommendationsForPosition', positionId]);
		},
		onError(error: Error) {
			onComplete();
			toast({
				title: `Error while performing this action: ${error.message}`,
				type: 'error',
			});
		},
	});

	const approveActionMutation = useMutation(['approveAction'], approveAction, {
		onSuccess() {
			toast({
				title: 'Action successfull!',
				type: 'success',
			});
			onComplete();
			queryClient.invalidateQueries(['getRecommendationsForPosition', positionId]);
			queryClient.refetchQueries(['getRecommendationsForPosition', positionId]);
		},
		onError(error: Error) {
			onComplete();
			toast({
				title: `Error while performing this action: ${error.message}`,
				type: 'error',
			});
		},
	});
	const [remarksDrawer, setRemarksDrawer] = useState(false);
	const [remarksDrawerType, setRemarksDrawerType] = useState<UIRemarksDrawerType>(
		UIRemarksDrawerType.SEND_OFFER_LETTER_CHANGES,
	);
	const [remarksDrawerCtaText, setRemarksDrawerCtaText] = useState<string>('Submit');
	const [remarks, setRemarks] = useState<string>('');
	const [ctc, setCtc] = useState<number>(0);
	const [designationTitle, setDesignationTitle] = useState<string>('');

	const [offerLetterDrawer, setOfferLetterDrawer] = useState(false);

	const handleSubmit = () => {
		setIsLoading(true);
		switch (remarksDrawerType) {
			case UIRemarksDrawerType.SEND_OFFER_LETTER_CHANGES:
				rejectActionMutation.mutate({
					applicationId: application?._id || '',
					positionId: positionId || '',
					approvalType: 'opt-out',
					remarks: remarks,
					newOfferedParameters: {
						positionTitle: designationTitle,
						ctc,
						remarks,
					},
				});
				break;
			case UIRemarksDrawerType.REQUEST_OFFER_CHANGES:
				rejectActionMutation?.mutate({
					applicationId: application?._id || '',
					positionId: positionId || '',
					approvalType: 'offer-letter',
					remarks,
				});
				break;
		}
	};
	// if (!application?.offerLetterLink) return null;

	const lastChatThread =
		application?.offerLetterApprovalChatThread?.[application?.offerLetterApprovalChatThread?.length - 1]?.data || '';
	const jobseekerOptOutRemarks = application?.jobseekerOptOutRemarks;
	const commentUI = (
		<>
			{lastChatThread && user?.role && [UserRole.RECRUITER].includes(user?.role) && (
				<div className="border border-[#C7C9D933] dark:bg-[#18222D] bg-[#E5F0FF] mt-[14px] rounded-[8px] p-[10px]">
					<p className="text-[#555770]">
						<span className="text-[#FF3B3B]">Comments: </span>
						{lastChatThread}
					</p>
				</div>
			)}
		</>
	);
	const isMobile = useMediaQuery('(max-width:960px)');

	return (
		<>
			{!isJobseekerLoggedIn && (
				<div className="text-lg font-inter flex font-medium items-center border-b pb-1">
					<span
						className="flex items-center cursor-pointer"
						onClick={() => {
							// navigate to previous page
							searchParams.delete('viewOffer');
							setSearchParams(searchParams);
						}}
					>
						<span className="mr-3 inline-block align-bottom">
							<Img loading="lazy" src="/assets/arrowLeft.svg" alt="arrowLeft" width="24" height="24" />
						</span>
						All Candidates
					</span>
				</div>
			)}
			<div className="mt-[18px] pb-[100px]">
				{([
					ApplicationStatus.CHANGED_OFFER_TERMS,
					ApplicationStatus.PLACED,
					ApplicationStatus.VERBALLY_PLACED,
					ApplicationStatus.OFFER_ROLLED_OUT,
				].includes(application?.status as ApplicationStatus) &&
					isJobseekerLoggedIn) ||
				[
					ApplicationStatus.CHANGED_OFFER_TERMS,
					ApplicationStatus.OFFER_ROLLED_OUT,
					ApplicationStatus.PLACED,
					ApplicationStatus.VERBALLY_PLACED,
					ApplicationStatus.PENDING_FOR_OFFER_LETTER_APPROVAL,
				].includes(application?.status as ApplicationStatus) ||
				((application?.adminRejectedOfferLetter || application?.pendingForAdminApproval) &&
					(isAccountManagerLoggedIn || isRecruiterLoggedIn)) ? (
					<OfferedPositionParameters
						companyName={application?.position?.company?.name ?? ''}
						jobType={application?.offeredPositionParameters?.offerType || positionWorkingMode}
						positionName={generatePositionTitle(application?.position?.title ?? '')}
						offeredCtc={application?.offeredPositionParameters?.ctc + ''}
						offeredDesignation={generatePositionTitle(application?.offeredPositionParameters?.positionTitle ?? '')}
						accManagerRemarks={application?.offeredPositionParameters?.remarks ?? ''}
						internshipCtc={application?.offeredPositionParameters?.internshipCtc ?? 0}
						internshipDuration={application?.offeredPositionParameters?.internshipDuration ?? 0}
						dateOfJoining={application?.offeredPositionParameters?.dateOfJoining ?? undefined}
					/>
				) : (
					<>
						<p className="font-inter text-[18px] font-[500] leading-[28px]">{`${companyName} - ${positionTitle} offer`}</p>
						{commentUI}
					</>
				)}

				{application?.offerLetterLink && (
					<div className="mt-[16px] h-[400px]">
						<Viewer fileUrl={application?.offerLetterLink} theme={themeMode} plugins={[defaultLayoutPluginInstance]} />
					</div>
				)}
				{isJobseekerLoggedIn &&
				application?.status !== ApplicationStatus.PENDING_FOR_OPT_OUT_APPROVAL &&
				application?.status !== ApplicationStatus.PLACED &&
				application?.status !== ApplicationStatus.VERBALLY_PLACED &&
				application?.status !== ApplicationStatus.PLACED_ELSEWHERE &&
				application?.history?.some(history => history.newStatus === ApplicationStatus.OFFER_ROLLED_OUT) ? (
					<div className="mt-[32px]">
						<div className="flex gap-[18px] items-center justify-between w-[100%]">
							<Button
								onClick={() => {
									setShowAcceptConfirmModal(true);
								}}
								color="purplePlaced"
								disabled={acceptChangedApplicationTermsMutation.isLoading || offerLetterResponseMutation.isLoading}
								size={'lg'}
								className="w-[100%]"
							>
								{acceptChangedApplicationTermsMutation.isLoading || offerLetterResponseMutation.isLoading ? (
									<Spinner />
								) : (
									'Accept Offer'
								)}
							</Button>
							<Button
								disabled={acceptChangedApplicationTermsMutation.isLoading || offerLetterResponseMutation.isLoading}
								onClick={() => {
									setShowDeclineConfirmModal(true);
								}}
								className="!bg-[#4B5563] hover:!bg-[#566171] !border !border-[#6B7280]] text-[#fff] w-[100%]"
								size={'lg'}
							>
								Decline Offer
							</Button>
						</div>
					</div>
				) : isJobseekerLoggedIn &&
				  application?.status === ApplicationStatus.VERBALLY_PLACED &&
				  !application?.offerLetterLink ? (
					<form className="mt-6" onSubmit={handleSubmitJobseekrOfferLetter}>
						<Label value="Upload offer letter" />
						<div className="max-w-[520px] mt-2">
							<FileInput
								id="file-upload"
								sizing="lg"
								helperText="Upload the offer letter shared by the company. Only txt, pdf, doc files allowed."
								accept=".pdf,doc,docx"
								disabled={isLoading || sendOfferLetterByJobseekerFn?.isLoading}
								onChange={event => {
									if (event.target.files) {
										console.log('event.target.files[0]:', event.target.files[0]);
										setFile && setFile(event.target.files[0]);
									}
								}}
							/>
						</div>
						<Button
							type="submit"
							size="lg"
							color="purplePlaced"
							className="mt-4 w-full md:max-w-[162px]"
							disabled={sendOfferLetterByJobseekerFn?.isLoading || !file}
						>
							{sendOfferLetterByJobseekerFn?.isLoading ? (
								<div className="absolute flex justify-center inset-0 rounded-[inherit] items-center w-full h-full backdrop-blur-md">
									<Spinner />
								</div>
							) : null}
							Submit
						</Button>
					</form>
				) : null}
				{(user?.role === UserRole.ACCOUNT_MANAGER || user?.role === UserRole.SUPER_ACCOUNT_MANAGER) &&
					application?.status === ApplicationStatus.VERBALLY_PLACED &&
					!(application?.adminRejectedOfferLetter || application?.pendingForAdminApproval) &&
					application?.offerLetterLink && (
						<div className="grid md:flex gap-[8px] mt-[16px]">
							<Button
								onClick={() => {
									approveActionMutation?.mutate({
										applicationId: application?._id || '',
										positionId: positionId || '',
										approvalType: 'offer-letter',
										remarks: '',
									});
								}}
								disabled={approveActionMutation?.isLoading || isLoading}
								className=""
								color="purplePlaced"
							>
								{approveActionMutation.isLoading ? <Spinner /> : 'Approve Offer Letter'}
							</Button>
							<Button
								onClick={() => {
									rejectActionMutation?.mutate({
										applicationId: application?._id || '',
										positionId: positionId || '',
										approvalType: 'offer-letter',
										remarks: '',
									});
								}}
								disabled={approveActionMutation?.isLoading || isLoading}
								color="light"
							>
								Reject Offer Letter
							</Button>
						</div>
					)}
				{(user?.role === UserRole.ACCOUNT_MANAGER || user?.role === UserRole.SUPER_ACCOUNT_MANAGER) &&
					application?.status === ApplicationStatus.PENDING_FOR_OFFER_LETTER_APPROVAL && (
						<div className="grid md:flex gap-[8px] mt-[16px]">
							<Button
								onClick={() => {
									approveActionMutation?.mutate({
										applicationId: application?._id || '',
										positionId: positionId || '',
										approvalType: 'offer-letter',
										remarks: '',
									});
								}}
								disabled={approveActionMutation?.isLoading}
								className=""
								color="purplePlaced"
							>
								{approveActionMutation.isLoading ? <Spinner /> : 'Approve'}
							</Button>
							<Button
								onClick={() => {
									setRemarksDrawer(true);
									setRemarksDrawerCtaText('Request Correction');
									setRemarksDrawerType(UIRemarksDrawerType.REQUEST_OFFER_CHANGES);
								}}
								disabled={isLoading}
								color="light"
							>
								Request Correction
							</Button>
						</div>
					)}
				{(user?.role === UserRole.ACCOUNT_MANAGER ||
					user?.role === UserRole.SUPER_ACCOUNT_MANAGER ||
					(user?.role === UserRole.RECRUITER && !jobSeekerUser?.flags.isMasaiStudent)) &&
					application?.status === ApplicationStatus.PENDING_FOR_OPT_OUT_APPROVAL && (
						<div className="mt-[24px]">
							<p className="text-[#000] font-inter text-[16px] font-[500] leading-[21px] mr-[8px]">
								JobSeeker rejected offer letter, you can proceed with user rejection or request another offer
							</p>
							{jobseekerOptOutRemarks && (
								<div className="mb-[16px] border border-[#C7C9D933] dark:bg-[#18222D] bg-[#E5F0FF] mt-[14px] rounded-[4px] p-[10px]">
									<p className="text-[#555770]">
										<span className="text-[#FF3B3B]">Comments: </span>
										{jobseekerOptOutRemarks}
									</p>
								</div>
							)}

							<div className="grid md:flex gap-[8px] mt-[16px]">
								<Button
									onClick={() => {
										setRemarksDrawer(true);
										setRemarksDrawerCtaText('Send offer letter changes');
										setRemarksDrawerType(UIRemarksDrawerType.SEND_OFFER_LETTER_CHANGES);
									}}
									className=""
									color="purplePlaced"
								>
									Send Offer Changes
								</Button>
								<Button
									onClick={() => {
										approveActionMutation?.mutate({
											applicationId: application?._id || '',
											positionId: positionId || '',
											approvalType: 'opt-out',
											remarks: '',
										});
									}}
									disabled={isLoading || approveActionMutation.isLoading}
									color="gray"
									className="border border-[#6B7280] bg-[#4B5563] text-[#fff]"
								>
									{approveActionMutation.isLoading ? <Spinner /> : 'Proceed With User Rejection'}
								</Button>
							</div>
						</div>
					)}
				{(isRecruiterLoggedIn || isAccountManagerLoggedIn) &&
					((!jobSeekerUser?.flags.isMasaiStudent ||
						application?.status === ApplicationStatus.PENDING_FOR_OPT_OUT_APPROVAL) &&
					application?.status &&
					[
						ApplicationStatus.PENDING_FOR_OPT_OUT_APPROVAL,
						ApplicationStatus.CHANGED_OFFER_TERMS,
						ApplicationStatus.PLACED,
						ApplicationStatus.VERBALLY_PLACED,
						ApplicationStatus.OFFER_REVOKED,
					].includes(application?.status) ? null : (
						<div>
							{application?.adminRejectedOfferLetter && application?.adminRejectionRemarks ? (
								<div className="max-w-[400px] mx-auto">
									<div className="border border-[#C7C9D933] dark:bg-[#18222D] bg-[#E5F0FF] mt-[14px] rounded-[8px] p-[10px]">
										<p className="text-[#555770]">
											<span className="text-yellow-600">Admin Comments: </span>
											{application?.adminRejectionRemarks}
										</p>
									</div>
								</div>
							) : null}
							<div className="grid md:flex gap-[8px] mt-[16px]">
								<Button
									className="mx-auto"
									disabled={application?.pendingForAdminApproval}
									onClick={() => {
										setOfferLetterDrawer(true);
									}}
									color="purplePlaced"
								>
									{application?.adminRejectedOfferLetter
										? 'Revise Offer'
										: application?.pendingForAdminApproval
										  ? 'In Review'
										  : 'Update Offer'}
								</Button>
							</div>
						</div>
					))}
			</div>
			<Modal show={showAcceptConfirmModal} onClose={() => setShowAcceptConfirmModal(false)} dismissible size="md">
				<Modal.Body className="rounded-[8px] border border-solid dark:border-[#4B5563] bg-white dark:bg-[#1F2A37]">
					<div className="text-center text-[16px] font-[600] text-[#111928] dark:text-[#F2F2F5]">
						Are you sure you want to accept this offer?
					</div>
					<div className="mt-[20px] flex justify-center gap-4">
						<Button color="light" onClick={() => setShowAcceptConfirmModal(false)} disabled={acceptChangedApplicationTermsMutation.isLoading || offerLetterResponseMutation.isLoading}>
							No, cancel
						</Button>
						<Button
							color="purplePlaced"
							disabled={acceptChangedApplicationTermsMutation.isLoading || offerLetterResponseMutation.isLoading}
							isProcessing={acceptChangedApplicationTermsMutation.isLoading || offerLetterResponseMutation.isLoading}
							onClick={() => {
								setShowAcceptConfirmModal(false);
								if (application?.status === ApplicationStatus.CHANGED_OFFER_TERMS) {
									acceptChangedApplicationTermsMutation.mutate({
										applicationId: application?._id || '',
										positionId: application?.position?._id || '',
									});
								} else {
									offerLetterResponseMutation.mutate({
										applicationId: application?._id || '',
										action: 'Accept',
									});
								}
							}}
						>
							Yes, accept
						</Button>
					</div>
				</Modal.Body>
			</Modal>
			<Modal show={showDeclineConfirmModal} onClose={() => setShowDeclineConfirmModal(false)} dismissible size="md">
				<Modal.Body className="rounded-[8px] border border-solid dark:border-[#4B5563] bg-white dark:bg-[#1F2A37]">
					<div className="text-center text-[16px] font-[600] text-[#111928] dark:text-[#F2F2F5]">
						Are you sure you want to decline this offer?
					</div>
					<div className="mt-[20px] flex justify-center gap-4">
						<Button color="light" onClick={() => setShowDeclineConfirmModal(false)} disabled={acceptChangedApplicationTermsMutation.isLoading || offerLetterResponseMutation.isLoading}>
							No, cancel
						</Button>
						<Button
							color="purplePlaced"
							disabled={acceptChangedApplicationTermsMutation.isLoading || offerLetterResponseMutation.isLoading}
							onClick={() => {
								setShowDeclineConfirmModal(false);
								setShowDeclineModal(true);
							}}
						>
							Yes, decline
						</Button>
					</div>
				</Modal.Body>
			</Modal>
			<OptOutModal
				isOpen={showDeclineModal}
				onClose={() => {
					setShowDeclineModal(false);
				}}
				optOutReason={declineReason}
				setOptOutReason={setDeclineReason}
				fromOfferLetter={true}
				application={application}
				offerLetterResponseMutation={offerLetterResponseMutation}
				requestOptingOutOfApplicationProcessMutation={requestOptingOutOfApplicationProcessMutation}
			/>
			<Drawer
				minHeight="max-content"
				maxHeight="90dvh"
				isModal={!isMobile}
				boxShadow="0px -8px 32px 0px #00000040"
				isOpen={remarksDrawer}
				onClose={() => {
					setRemarksDrawer(false);
				}}
				header={
					<h1 className="text-[#1C1C28] dark:text-inherit text-lg font-inter font-[500] px-5 pt-4">
						{remarksDrawerCtaText}
					</h1>
				}
				body={
					<div className="px-3 py-4">
						{
							<RemarksDrawer
								remarks={remarks}
								setRemarks={setRemarks}
								designationTitle={designationTitle}
								setDesignationTitle={setDesignationTitle}
								ctc={ctc}
								setCtc={setCtc}
								isLoading={isLoading}
								ctaText={remarksDrawerCtaText}
								type=""
								handleSubmit={handleSubmit}
								remarksDrawerType={remarksDrawerType}
								jobseekerOptOutRemarks={jobseekerOptOutRemarks}
							/>
						}
					</div>
				}
			/>
			<Drawer
				minHeight="max-content"
				maxHeight="90dvh"
				boxShadow="0px -8px 32px 0px #00000040"
				isOpen={offerLetterDrawer}
				isModal={!isMobile}
				onClose={() => {
					setOfferLetterDrawer(false);
				}}
				header={
					<>
						<h1 className="text-[#1C1C28] dark:text-inherit text-lg font-inter font-[500] px-5 pt-4">Update offer:</h1>
						<div className="px-[24px]">{commentUI}</div>
					</>
				}
				body={
					<div className="px-3 py-4">
						{
							<RecruiterCandidateSendOfferLetter
								userName={application?.user?.name ?? ''}
								offeredPositionParameters={application?.offeredPositionParameters}
								offerLetterLink={application?.offerLetterLink}
								applicationId={application?._id ?? ''}
								positionId={positionId ?? ''}
								onFinish={() => {
									setOfferLetterDrawer(false);
								}}
							/>
						}
					</div>
				}
			/>
		</>
	);
};

export default ViewOffer;
